import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  DateInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator
} from 'react-admin';
import { EntityMetadata, EntityFieldMetadata } from '../../services/types/entity-discovery.types';

interface DynamicEntityCreateProps {
  entityMetadata: EntityMetadata;
}

export const DynamicEntityCreate: React.FC<DynamicEntityCreateProps> = ({ entityMetadata }) => {
  const renderInput = (field: EntityFieldMetadata) => {
    // Skip read-only fields
    if (field.name === '_id' || field.name === 'createdAt' || field.name === 'updatedAt') {
      return null;
    }

    const commonProps = {
      key: field.name,
      source: field.name,
      label: formatFieldLabel(field.name),
      required: field.required,
      defaultValue: field.default
    };

    // Handle arrays
    if (field.isArray) {
      return (
        <ArrayInput {...commonProps}>
          <SimpleFormIterator>
            {renderSingleInput(field, { ...commonProps, source: '' })}
          </SimpleFormIterator>
        </ArrayInput>
      );
    }

    return renderSingleInput(field, commonProps);
  };

  const renderSingleInput = (field: EntityFieldMetadata, props: any) => {
    // Handle enums
    if (field.enum && field.enum.length > 0) {
      const choices = field.enum.map(value => ({ id: value, name: value }));
      return <SelectInput {...props} choices={choices} />;
    }

    // Handle references
    if (field.ref) {
      return (
        <ReferenceInput 
          {...props} 
          reference={field.ref.toLowerCase()}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
      );
    }

    // Handle different field types
    switch (field.type.toLowerCase()) {
      case 'number':
      case 'decimal128':
        return <NumberInput {...props} />;
      
      case 'boolean':
        return <BooleanInput {...props} />;
      
      case 'date':
        return <DateInput {...props} />;
      
      case 'string':
      default:
        // For nested objects (like address), provide a multiline input
        if (field.name.toLowerCase().includes('address') || 
            field.name.toLowerCase().includes('description')) {
          return <TextInput {...props} multiline rows={3} />;
        }
        // For email fields, use email input type
        if (field.name.toLowerCase().includes('email')) {
          return <TextInput {...props} type="email" />;
        }
        // For phone fields, use tel input type
        if (field.name.toLowerCase().includes('phone')) {
          return <TextInput {...props} type="tel" />;
        }
        // For URL fields, use url input type
        if (field.name.toLowerCase().includes('url')) {
          return <TextInput {...props} type="url" />;
        }
        return <TextInput {...props} />;
    }
  };

  // Get fields for create form, excluding read-only fields
  const getCreateFields = () => {
    return entityMetadata.fields.filter(field => 
      !['_id', 'createdAt', 'updatedAt'].includes(field.name)
    );
  };

  const fieldsToShow = getCreateFields();

  return (
    <Create title={`Create ${entityMetadata.name}`}>
      <SimpleForm>
        {fieldsToShow.map(field => renderInput(field))}
      </SimpleForm>
    </Create>
  );
};

/**
 * Convert field name from camelCase to human readable format
 */
function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}
